import { useEffect, useState, type ReactNode } from 'react';
import type { CursorPage, EmployeeRow, EmploymentStatus } from '@dru/types';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  cn, DataTable, EmptyState, IconAction, RowActions, SearchInput, StatusBadge, type Tone,
} from '@dru/ui';
import { EMPLOYMENT_STATUS_LABEL } from '@dru/types';
import { Eye, Pencil, QrCode, Upload, UserPlus, UserX } from 'lucide-react';
import { useAuth } from '../../../lib/auth';
import { api } from '../../../lib/api';
import { usePages } from '../../../lib/usePages';
import { PageHeader } from '../../../layouts/ConsoleShell';
import { formatDate } from '../../../lib/format';

/** A link that reads as a button, for actions that are navigation. */
const LINK_BTN = 'inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-btn px-4 text-body font-semibold transition-colors duration-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1';
const LINK_PRIMARY = cn(LINK_BTN, 'bg-primary-600 text-white hover:bg-primary-700');
const LINK_SECONDARY = cn(LINK_BTN, 'border border-neutral-300 bg-white text-neutral-800 hover:bg-neutral-50');

const statusTone = (s: string): Tone =>
  s === 'ACTIVE' ? 'covered'
  : s === 'CANDIDATE' ? 'info'
  : s === 'SUSPENDED' ? 'warning'
  : s === 'ON_LEAVE' ? 'pending'
  : 'neutral';

/**
 * The workforce register: employees and pre-employment candidates.
 *
 * Two ways in at the top (add one, import a file), one search box, one row
 * of status chips, and a table in the template's column order. Search and
 * status live in the URL, so the dashboard's links and a colleague's pasted
 * link land on the same rows. No plant column or filter: the header's plant
 * switcher decides which plant a company-wide login is looking at (owner,
 * 26 Oct 2026); `?plantId=` from a link is still honoured.
 */
const SEX_LABEL: Record<string, string> = { MALE: 'Male', FEMALE: 'Female', OTHER: 'Other' };

/** The statuses HR filters by day to day; the rest appear only when a link asks for them. */
const QUICK_STATUSES: EmploymentStatus[] = ['ACTIVE', 'CANDIDATE', 'ON_LEAVE', 'SUSPENDED', 'EXITED'];

const statusLabel = (s: string) => EMPLOYMENT_STATUS_LABEL[s as EmploymentStatus] ?? s;

export function Employees() {
  const nav = useNavigate();
  const { permissions } = useAuth();
  const [params, setParams] = useSearchParams();
  const plantId = params.get('plantId') ?? '';
  const status = params.get('status') ?? '';
  const applied = params.get('q') ?? '';
  const [search, setSearch] = useState(applied);

  const setParam = (key: string, value: string) => setParams((p) => {
    const n = new URLSearchParams(p);
    if (value) n.set(key, value); else n.delete(key);
    return n;
  }, { replace: key === 'q' });

  // Debounced: one request per pause, not one per keystroke against a table
  // with a company's whole workforce in it.
  useEffect(() => {
    const t = setTimeout(() => { if (search.trim() !== applied) setParam('q', search.trim()); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, applied]);

  const canRead = permissions.has('employee.read');
  // `q`, not `search`: the API's ListEmployeesSchema names this parameter `q`
  // and Zod strips what it does not know.
  const page = usePages<EmployeeRow>(
    (cursor) => (canRead ? api.get<CursorPage<EmployeeRow>>(
      `/employees?limit=50${applied ? `&q=${encodeURIComponent(applied)}` : ''}`
      + `${plantId ? `&plantId=${plantId}` : ''}${status ? `&status=${status}` : ''}`
      + `${cursor ? `&cursor=${cursor}` : ''}`) : Promise.resolve({ items: [], nextCursor: null })),
    [applied, plantId, status, canRead],
  );

  const filtered = Boolean(applied || plantId || status);
  const canImport = permissions.has('employee.import');
  const canCreate = permissions.has('employee.create');
  const canEdit = permissions.has('employee.update');
  const canRemove = permissions.has('employee.deactivate');

  const clearFilters = () => {
    setSearch('');
    setParams((p) => { const n = new URLSearchParams(p); ['q', 'status', 'plantId'].forEach((k) => n.delete(k)); return n; });
  };

  // A status reached by a link but not among the quick chips gets its own chip,
  // so the filter in force is always visible and can be cleared.
  const chips = status && !QUICK_STATUSES.includes(status as EmploymentStatus)
    ? [...QUICK_STATUSES, status as EmploymentStatus] : QUICK_STATUSES;

  if (!canRead) {
    return (
      <div>
        <PageHeader title="Employees" />
        <EmptyState title="The employee register is not part of your role" hint="Ask your company administrator for access." />
      </div>
    );
  }

  const count = page.items.length;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Employees"
        hint="Employees and pre-employment candidates. Select a row to open the profile."
        actions={(canImport || canCreate) && (
          <>
            {canImport && (
              <Link to="/hr/employees/import" className={cn(LINK_SECONDARY, 'max-sm:flex-1')}>
                <Upload aria-hidden className="h-4 w-4" />
                <span className="whitespace-nowrap"><span className="sm:hidden">Import file</span><span className="max-sm:hidden">Import Excel/CSV</span></span>
              </Link>
            )}
            {canCreate && (
              <Link to="/hr/employees/new" className={cn(LINK_PRIMARY, 'max-sm:flex-1')}>
                <UserPlus aria-hidden className="h-4 w-4" /> Add employee
              </Link>
            )}
          </>
        )}
      />

      <div className="space-y-3">
        <form role="search" className="w-full sm:max-w-sm" onSubmit={(e) => { e.preventDefault(); setParam('q', search.trim()); }}>
          <SearchInput
            value={search}
            onValueChange={setSearch}
            aria-label="Search employees by name or employee ID"
            placeholder="Search by name or employee ID"
          />
        </form>

        {/* One tap per status. Scrolls sideways on a phone rather than wrapping. */}
        <div role="group" aria-label="Filter by status"
             className="flex gap-2 max-md:-mx-4 max-md:overflow-x-auto max-md:px-4 max-md:pb-1 md:flex-wrap">
          <StatusChip active={!status} onClick={() => setParam('status', '')}>All</StatusChip>
          {chips.map((s) => (
            <StatusChip key={s} active={status === s} onClick={() => setParam('status', status === s ? '' : s)}>
              {statusLabel(s)}
            </StatusChip>
          ))}
        </div>

        {!page.loading && !page.error && (count > 0 || filtered) && (
          <p data-testid="employee-count" className="flex flex-wrap items-center gap-x-3 gap-y-1 text-small text-neutral-600" aria-live="polite">
            <span>
              {count === 0 ? 'No employees' : `${count.toLocaleString('en-IN')}${page.hasMore ? '+' : ''} employee${count === 1 && !page.hasMore ? '' : 's'}`}
              {applied && <> matching “{applied}”</>}
              {status && <> · {statusLabel(status)}</>}
            </span>
            {filtered && (
              <button type="button" onClick={clearFilters}
                      className="font-medium text-primary-600 hover:text-primary-700 hover:underline max-md:min-h-10">
                Clear filters
              </button>
            )}
          </p>
        )}
      </div>

      <DataTable<EmployeeRow>
        rows={page.items}
        loading={page.loading}
        error={page.error}
        onRetry={page.reload}
        hasMore={page.hasMore}
        onLoadMore={page.loadMore}
        loadingMore={page.loadingMore}
        rowKey={(e) => e.id}
        onRowClick={(e) => nav(`/hr/employees/${e.id}`)}
        empty={{
          title: filtered ? 'No one matches' : 'No employees yet',
          hint: filtered
            ? 'Try a different name or employee ID, or clear the filters.'
            : canImport || canCreate
              ? 'Add employees one at a time, or import your whole workforce from an Excel or CSV file.'
              : 'Employees appear here once your company administrator adds them.',
          action: filtered ? (
            <button type="button" onClick={clearFilters} className={LINK_SECONDARY}>Clear filters</button>
          ) : canImport ? (
            <Link to="/hr/employees/import" className={LINK_PRIMARY}><Upload aria-hidden className="h-4 w-4" /> Import Excel/CSV</Link>
          ) : canCreate ? (
            <Link to="/hr/employees/new" className={LINK_PRIMARY}><UserPlus aria-hidden className="h-4 w-4" /> Add employee</Link>
          ) : undefined,
        }}
        columns={[
          // ID, name, mobile, gender, date of birth, blood group — the
          // template's order (owner, 26 Oct 2026) — then status.
          {
            key: 'code', header: 'Employee ID',
            cell: (e) => <span className="whitespace-nowrap font-mono text-small text-neutral-700">{e.employeeCode}</span>,
          },
          {
            key: 'name', header: 'Name',
            cell: (e) => <span className="font-medium text-neutral-900">{e.fullName}</span>,
          },
          {
            key: 'mobile', header: 'Mobile',
            cell: (e) => <span className="whitespace-nowrap font-mono text-small text-neutral-600">{e.mobile ?? '—'}</span>,
          },
          {
            // Gender and blood group give way first on a tablet, so the row's
            // actions stay in view; the profile has both.
            key: 'sex', header: 'Gender', hideOnMobile: true, className: 'max-lg:!hidden',
            cell: (e) => <span className="text-small text-neutral-700">{e.sex ? SEX_LABEL[e.sex] ?? e.sex : '—'}</span>,
          },
          {
            key: 'dob', header: 'Date of birth', hideOnMobile: true,
            cell: (e) => <span className="whitespace-nowrap text-small tabular-nums text-neutral-700">{e.dateOfBirth ? formatDate(e.dateOfBirth) : '—'}</span>,
          },
          {
            key: 'blood', header: 'Blood group', hideOnMobile: true, className: 'max-lg:!hidden',
            cell: (e) => <span className="text-small text-neutral-700">{e.bloodGroup ?? '—'}</span>,
          },
          {
            key: 'status', header: 'Status', mobileLabel: false,
            cell: (e) => <StatusBadge tone={statusTone(e.status)}>{statusLabel(e.status)}</StatusBadge>,
          },
          {
            // One icon per act, each named in its tooltip. A click on an icon
            // is that act, not a click on the row.
            key: 'actions', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '',
            cell: (e) => (
              <RowActions>
                <IconAction label={`View ${e.fullName}`} icon={Eye} onClick={() => nav(`/hr/employees/${e.id}`)} />
                {canEdit && <IconAction label={`Edit ${e.fullName}`} icon={Pencil} onClick={() => nav(`/hr/employees/${e.id}?action=edit`)} />}
                {/* Same tab, as from the profile: a new tab loses an impersonation session. */}
                <IconAction label={`Print QR card for ${e.fullName}`} icon={QrCode} onClick={() => nav(`/hr/employees/${e.id}/print-qr`)} />
                {canRemove && !['EXITED', 'NOT_HIRED', 'CANDIDATE'].includes(e.status) && (
                  <IconAction label={`Deactivate ${e.fullName} (record exit)`} icon={UserX} danger onClick={() => nav(`/hr/employees/${e.id}?action=exit`)} />
                )}
              </RowActions>
            ),
          },
        ]}
      />
    </div>
  );
}

function StatusChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex h-8 shrink-0 items-center rounded-full border px-3.5 text-small font-medium transition-colors duration-hover max-md:h-10',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
        active
          ? 'border-primary-600 bg-primary-600 text-white'
          : 'border-neutral-300 bg-white text-neutral-700 hover:border-primary-300 hover:bg-primary-50',
      )}
    >
      {children}
    </button>
  );
}
